'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GalleryItem {
  id: number;
  before: {
    src: string;
    alt: string;
  };
  after: {
    src: string;
    alt: string;
  };
  title: string;
  description: string;
  patioType: string;
}

interface BeforeAfterGalleryProps {
  galleryItems: GalleryItem[];
}

export default function BeforeAfterGallery({ galleryItems }: BeforeAfterGalleryProps) {
  const [selectedItem, setSelectedItem] = useState<number | null>(null);

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl sm:text-4xl text-primary mb-4">
            Transformation Gallery
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            See the dramatic difference our expert cleaning makes across various patio types and conditions throughout the Phoenix Metro area.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryItems.map((item) => (
            <div
              key={item.id}
              className="bg-card rounded-lg overflow-hidden shadow-brand hover:shadow-brand-elevated transition-brand cursor-pointer"
              onClick={() => setSelectedItem(item.id)}
            >
              <div className="grid grid-cols-2 gap-0.5">
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute top-2 left-2 bg-destructive text-white text-xs font-body font-semibold px-2 py-1 rounded z-10">
                    BEFORE
                  </div>
                  <AppImage
                    src={item.before.src}
                    alt={item.before.alt}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute top-2 right-2 bg-success text-white text-xs font-body font-semibold px-2 py-1 rounded z-10">
                    AFTER
                  </div>
                  <AppImage
                    src={item.after.src}
                    alt={item.after.alt}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-headline font-semibold text-base text-foreground">
                    {item.title}
                  </h3>
                  <span className="text-xs text-accent font-body font-medium px-2 py-1 bg-accent/10 rounded">
                    {item.patioType}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {selectedItem !== null && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedItem(null)}
          >
            <div
              className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {galleryItems
                .filter((item) => item.id === selectedItem)
                .map((item) => (
                  <div key={item.id} className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-headline font-bold text-2xl text-foreground">
                        {item.title}
                      </h3>
                      <button
                        onClick={() => setSelectedItem(null)}
                        className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-brand"
                        aria-label="Close gallery"
                      >
                        <Icon name="XMarkIcon" size={24} className="text-foreground" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <div className="relative h-96 rounded-lg overflow-hidden mb-2">
                          <AppImage
                            src={item.before.src}
                            alt={item.before.alt}
                            fill
                            className="object-cover"
                          />
                        </div>
                        <p className="text-center text-sm font-body font-semibold text-destructive">
                          BEFORE
                        </p>
                      </div>
                      <div>
                        <div className="relative h-96 rounded-lg overflow-hidden mb-2">
                          <AppImage
                            src={item.after.src}
                            alt={item.after.alt}
                            fill
                            className="object-cover"
                          />
                        </div>
                        <p className="text-center text-sm font-body font-semibold text-success">
                          AFTER
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-border pt-4">
                      <div className="flex items-center space-x-4 mb-3">
                        <span className="text-sm text-primary font-body font-semibold uppercase tracking-wide">
                          Patio Type:
                        </span>
                        <span className="text-sm text-foreground font-body">
                          {item.patioType}
                        </span>
                      </div>
                      <p className="text-base text-muted-foreground font-body leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}